/**
 * Feature/permission guards for Payment activity and file imports, shared by
 * azul and atlas so both apps gate these screens the same way.
 */
import { featureEnabled } from "../../../../Feature";

import type { Features } from "../../../../Feature";

type PaymentActivityFeatures = Partial<
  Pick<
    Features,
    | "payment_activity"
    | "business_banking"
    | "ach_payments"
    | "ach_payments_collect"
    | "bulk_payments"
    | "wires"
    | "international_wires"
    | "ach_file_import"
    | "ach_nacha_passthrough"
  >
>;

// Send or collect is enough; direction is enforced at validation.
export const ACH_PASSTHROUGH_PERMISSIONS = [
  "indigo.ACHPassthroughSend_permission",
  "indigo.ACHPassthroughCollect_permission",
];

export const canUseAchPassthrough = (features: Features) =>
  Boolean(features.ach_nacha_passthrough) &&
  featureEnabled(features, { or: ACH_PASSTHROUGH_PERMISSIONS });

const ACH_PASSTHROUGH_DIRECTION_PERMISSIONS: Record<
  API.NachaPassthrough.Direction,
  string[]
> = {
  send: ["indigo.ACHPassthroughSend_permission"],
  collect: ["indigo.ACHPassthroughCollect_permission"],
  both: ACH_PASSTHROUGH_PERMISSIONS,
};

/**
 * Whether the user holds the pass-through permission(s) a file's direction
 * needs to be approved or rejected: send, collect, or both for a file that
 * does both. Mirrors the backend's `can_approve_passthrough_direction`.
 */
export const canApproveAchPassthroughDirection = (
  features: Features,
  direction: API.NachaPassthrough.Direction,
) =>
  featureEnabled(features, {
    and: ACH_PASSTHROUGH_DIRECTION_PERMISSIONS[direction],
  });

export const canShowPaymentActivity = (
  features: PaymentActivityFeatures | null | undefined,
) => {
  if (!features) {
    return false;
  }

  if (features.payment_activity) {
    return true;
  }

  if (features.business_banking && features.bulk_payments) {
    return true;
  }

  // Pass-through-only businesses have neither flag above enabled.
  return Boolean(features.business_banking) && canUseAchPassthrough(features);
};

export const canShowIndividualPaymentActivity = (
  features: PaymentActivityFeatures | null | undefined,
) => {
  if (!features) {
    return false;
  }

  return Boolean(features.payment_activity);
};

export const ACH_FILE_IMPORT_PERMISSIONS = [
  "indigo.ACHPaymentViaImport_permission",
  "indigo.ACHPaymentCollectViaImport_permission",
];

const ACH_FILE_IMPORT_DOWNLOAD_PERMISSION =
  "indigo.ACHImportFileDownloads_permission";

export const canStartAchFileImport = (features: Features) =>
  Boolean(features.ach_file_import && features.bulk_payments) &&
  featureEnabled(features, { or: ACH_FILE_IMPORT_PERMISSIONS });

export const canViewAchFileImports = (features: Features) =>
  Boolean(features.ach_file_import) &&
  featureEnabled(features, {
    or: [...ACH_FILE_IMPORT_PERMISSIONS, ACH_FILE_IMPORT_DOWNLOAD_PERMISSION],
  });
